import { BehaviorSubject } from "rxjs";
import { CurrencyRateProvider, CurrencyService } from "../domain/services/CurrencyService";
import localStorageServiceFactory from "./LocalStorageServiceImpl";
import _ from "lodash";
import { Currency } from "../domain/enums/CurrencyEnum";
import { PriceDTO } from "../domain/models/dto/PriceDTO";

export const CURRENCIES = [Currency.UAH, Currency.USD, Currency.EUR];
const DEFAULT_CURRENCY = Currency.UAH;

export class CurrencyServiceImpl implements CurrencyService {
    private constructor() {
        const stored = localStorageServiceFactory().getCurrency();
        this._currentCurrency = CURRENCIES.includes(stored) ? stored : DEFAULT_CURRENCY;
        CurrencyServiceImpl.currency$.next(this._currentCurrency);
    }

    private static _instance: CurrencyService;
    private static PAYMENTS_DECIMAL_PLACES = 2;
    private _currentCurrency: Currency = DEFAULT_CURRENCY;
    private _currencies: Currency[] = CURRENCIES;
    private static rateProvider?: CurrencyRateProvider;

    static currency$ = new BehaviorSubject(DEFAULT_CURRENCY);

    static setRateProvider(provider?: CurrencyRateProvider): void {
        CurrencyServiceImpl.rateProvider = provider;
    }

    public static getInstance(): CurrencyService {
        if (!CurrencyServiceImpl._instance) {
            CurrencyServiceImpl._instance = new CurrencyServiceImpl();
        }
        return CurrencyServiceImpl._instance;
    }

    static floatToFixedPoint(
        num: number,
        decimalPlaces: number = CurrencyServiceImpl.PAYMENTS_DECIMAL_PLACES,
    ) {
        const multiplier = Math.pow(10, decimalPlaces);
        return _.ceil(_.ceil(num, decimalPlaces) * multiplier, decimalPlaces);
    }

    static floatToStr(number: number) {
        if (typeof number !== "number") {
            return "";
        }
        const result = number.toString();
        if (Number.isInteger(number)) {
            return result + ".00";
        }
        if (
            _.round(
                number % 1,
                CurrencyServiceImpl.PAYMENTS_DECIMAL_PLACES,
            ).toString().length === 3
        ) {
            return result + "0";
        } else {
            return result;
        }
    }

    static fixedPointToFloat(
        num: number,
        decimalPlaces: number = CurrencyServiceImpl.PAYMENTS_DECIMAL_PLACES,
    ) {
        return num / Math.pow(10, decimalPlaces);
    }

    static fixedPointToFloatStr(num: number) {
        if (typeof num !== "number") {
            return "";
        }
        const number = CurrencyServiceImpl.fixedPointToFloat(
            num,
            CurrencyServiceImpl.PAYMENTS_DECIMAL_PLACES,
        );
        const result = CurrencyServiceImpl.floatToStr(number);
        return result;
    }

    get currentCurrency(): Currency {
        return this._currentCurrency;
    }

    get currencies(): Currency[] {
        return this._currencies;
    }

    setCurrentCurrency(value: Currency): void {
        if (!CURRENCIES.includes(value)) return;
        this._currentCurrency = value;
        localStorageServiceFactory().setCurrency(value as Currency);
        CurrencyServiceImpl.currency$.next(value);
    }

    isCurrentCurrency(currency: Currency): boolean {
        return currency === this.currentCurrency;
    }

    convertCurrency(price: PriceDTO, target: Currency) {
        const source = price.currency;
        if (source === target) return { available: true as const, price: { ...price } };

        const rate = CurrencyServiceImpl.rateProvider?.getRate(source, target);
        if (typeof rate !== "number" || !Number.isFinite(rate) || rate <= 0) {
            return { available: false as const, source, target };
        }

        return {
            available: true as const,
            price: {
                ...price,
                currency: target,
                amount: CurrencyServiceImpl.fixedPointToFloat(
                    Math.round(price.amount * rate * 100),
                ),
            },
        };
    }
}

export default function currencyServiceFactory(): CurrencyService {
    return CurrencyServiceImpl.getInstance();
}
